import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { rrFetch } from "@/lib/rehearsalRoomAuth";

/**
 * Master v5 Prompt 25 — Annotated Text for Voice. Mirrors
 * workDocumentQueries.js's `request()` exactly (`.status`/`.body` on a
 * thrown Error) so a 409 "beat_locked" or a 409 stale-revision conflict
 * can be distinguished by callers the same way a WorkDocument block's
 * lease/save errors already are.
 */
async function request(path, options = {}) {
  const res = await rrFetch(path, options);
  if (res.status === 204) return null;
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const err = new Error((body && (body.detail?.error || body.detail?.message || body.detail)) || "request_failed");
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

const jsonPost = (path, payload) => request(path, { method: "POST", body: JSON.stringify(payload ?? {}) });
const jsonPatch = (path, payload) => request(path, { method: "PATCH", body: JSON.stringify(payload) });

export const atqk = {
  byInstance: (wrkId, iinId) => ["annotated-text-session", wrkId, iinId],
};

export function useAnnotatedTextSession(workspaceId, instanceId) {
  return useQuery({
    queryKey: atqk.byInstance(workspaceId, instanceId),
    enabled: !!workspaceId && !!instanceId,
    refetchInterval: 8000, // polling fallback — the same interval WorkDocument detail polling uses
    queryFn: () => request(`/api/rehearsal-room/workspaces/${workspaceId}/annotated-text-sessions/by-instance/${instanceId}`),
  });
}

export function useCreateAnnotatedTextSession(workspaceId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ asgId, sectionId }) => jsonPost(`/api/rehearsal-room/workspaces/${workspaceId}/annotated-text-sessions`, { asg_id: asgId, section_id: sectionId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["annotated-text-session", workspaceId] }),
  });
}

// -- Lease + annotation save: called directly (not via useMutation) by
// useAnnotationLease so it retains full control of error branching
// (locked vs conflict vs denied), exactly as acquireBlockLease/
// saveBlockContent already do for the WorkDocument engine.
export async function acquireAnnotationLease(workspaceId, atxId, beatId) {
  return request(`/api/rehearsal-room/workspaces/${workspaceId}/annotated-text-sessions/${atxId}/beats/${beatId}/lease`, { method: "POST" });
}

export async function heartbeatAnnotationLease(workspaceId, atxId, beatId, leaseId, leaseToken) {
  return request(`/api/rehearsal-room/workspaces/${workspaceId}/annotated-text-sessions/${atxId}/beats/${beatId}/lease/${leaseId}/heartbeat`, {
    method: "POST", body: JSON.stringify({ lease_token: leaseToken }),
  });
}

export async function releaseAnnotationLease(workspaceId, atxId, beatId, leaseId, leaseToken) {
  return request(`/api/rehearsal-room/workspaces/${workspaceId}/annotated-text-sessions/${atxId}/beats/${beatId}/lease/${leaseId}/release`, {
    method: "POST", body: JSON.stringify({ lease_token: leaseToken }),
  });
}

export async function saveAnnotation(workspaceId, atxId, beatId, { danId, noteText, expectedRevision, leaseId, leaseToken, idempotencyKey }) {
  return jsonPatch(`/api/rehearsal-room/workspaces/${workspaceId}/annotated-text-sessions/${atxId}/beats/${beatId}/annotation`, {
    dan_id: danId, note_text: noteText, expected_revision: expectedRevision, lease_id: leaseId, lease_token: leaseToken, idempotency_key: idempotencyKey,
  });
}
